import React, {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {fetchPeriods} from './redux/periodActions';
import {selectSchoolDetails} from '../../../../common';
import useBellSound from './useBellSound';
import bellSounds from './bellSoundsLocal';

/**
 * GlobalPeriodBellScheduler
 * Mount this once (e.g. in index.js) to have bell ringing happen in the background
 * irrespective of the current route or whether the PeriodManagement page is open.
 */
const GlobalPeriodBellScheduler = () => {
    const dispatch = useDispatch();
    const periods = useSelector(state => state?.period?.periods ?? state?.master?.data?.periods ?? []);
    const schoolDetails = useSelector(selectSchoolDetails);
    const schoolId = schoolDetails?.id;
    const session = schoolDetails?.session;

    const {playSound} = useBellSound();
    const timerRef = useRef(null);
    const [autoBellEnabled, setAutoBellEnabled] = useState(() => {
        const stored = localStorage.getItem('autoBellEnabled');
        return stored === null ? true : stored === 'true';
    });
    const [selectedBellSound, setSelectedBellSound] = useState(() => {
        const stored = localStorage.getItem('selectedBellSound');
        if (stored && bellSounds[stored]) return stored;
        return 'standard';
    });

    // Mark global scheduler active to let page-level component skip its own scheduling.
    useEffect(() => {
        window.__GLOBAL_PERIOD_BELL_SCHEDULER = true;
        return () => {
            delete window.__GLOBAL_PERIOD_BELL_SCHEDULER;
        };
    }, []);

    // Fetch periods if not already loaded when school/session becomes available.
    useEffect(() => {
        if (schoolId && session) {
            dispatch(fetchPeriods(schoolId, session));
        }
    }, [schoolId, session, dispatch]);

    // Listen for storage changes (multi-tab sync)
    useEffect(() => {
        const handler = (e) => {
            if (e.key === 'autoBellEnabled') setAutoBellEnabled(e.newValue === 'true');
            if (e.key === 'selectedBellSound' && bellSounds[e.newValue]) setSelectedBellSound(e.newValue);
        };
        window.addEventListener('storage', handler);
        return () => window.removeEventListener('storage', handler);
    }, []);

    // Request notification permission (non-blocking)
    useEffect(() => {
        if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
            try {
                Notification.requestPermission();
            } catch (_) {
            }
        }
    }, []);

    const clearExistingTimer = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };

    // Helper: get weekday name matching stored structure
    const weekdayName = (date) => ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][date.getDay()];

    // Compute next event (start or end) across up to next 7 days
    const findNextEvent = (now) => {
        let nextEvent = null;
        for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
            const targetDate = new Date(now);
            targetDate.setDate(now.getDate() + dayOffset);
            const dayName = weekdayName(targetDate);

            periods.forEach(period => {
                if (!period) return;

                // Support both data formats:
                // 1. period has nested days object (period.days.monday)
                // 2. period has direct boolean fields (period.monday)
                const isDayActive = (period.days && period.days[dayName] === true) ||
                                   (period[dayName] === true);

                if (!isDayActive) return;

                ['startTime', 'endTime'].forEach(kind => {
                    const t = period[kind];
                    if (!t) return;
                    const [h, m] = t.split(':').map(Number);
                    const eventDate = new Date(targetDate);
                    eventDate.setHours(h, m, 0, 0);
                    // Skip past events if same day
                    if (dayOffset === 0 && eventDate <= now) return;
                    if (!nextEvent || eventDate < nextEvent.when) {
                        nextEvent = {
                            when: eventDate,
                            period,
                            type: kind === 'startTime' ? 'start' : 'end',
                            timeString: t
                        };
                    }
                });
            });
            if (nextEvent && nextEvent.when.getDate() === targetDate.getDate()) {
                // We found the earliest for the soonest possible day; can still break because earlier day offsets override later ones.
                // However we must still ensure there's no earlier event within same day (already considered). Safe to break here.
                break;
            }
        }
        return nextEvent;
    };

    const scheduleNext = () => {
        clearExistingTimer();
        if (!autoBellEnabled || periods.length === 0) return;
        const now = new Date();
        const next = findNextEvent(now);
        if (!next) return; // Nothing scheduled in next 7 days
        const delay = next.when.getTime() - now.getTime();
        if (delay < 0) return; // Safety

        console.log('[GlobalBellScheduler] Next bell in', Math.round(delay / 1000), 'seconds for', next.period.name, next.type);

        timerRef.current = setTimeout(() => {
            // Play sound
            playSound(selectedBellSound).catch(err => console.error('Bell playback failed', err));

            // Browser notification
            if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
                const title = next.type === 'start' ? `${next.period.name} is starting` : `${next.period.name} ended`;
                try {
                    new Notification('School Bell', {body: title, silent: false});
                } catch (e) {
                }
            }

            // Schedule subsequent bell
            scheduleNext();
        }, delay);
    };

    // Reschedule when dependencies change
    useEffect(() => {
        scheduleNext();
        return clearExistingTimer;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [periods, autoBellEnabled, selectedBellSound]);

    return null; // No UI
};

export default GlobalPeriodBellScheduler;

